import { Button, Stack, TextField, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import { useMutateCreateCropMonitoringNote } from '/@/hooks/crop-monitoring-notes';
import { _ } from '/@/locale';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import {
    errorDetail,
    flowEventProps,
    trackFormAbandoned,
    useFieldRegistryFlow,
    useFormExitTracking,
} from './flow-tracking';
import type { RegistryAddStep } from './types';

const CreateRegistryNote = ({
    open,
    onClose,
    onSuccess,
    setStep,
    fieldId,
}: {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    setStep: (step: RegistryAddStep) => void;
    fieldId?: number;
}) => {
    const createNote = useMutateCreateCropMonitoringNote();
    const [content, setContent] = useState('');
    // Errors show once the user tries to register; before that the form starts clean.
    const [showErrors, setShowErrors] = useState(false);
    const contentError = showErrors && !content.trim();

    const flow = useFieldRegistryFlow();
    const { end, finish } = useFormExitTracking((exitMethod) => {
        if (flow) trackFormAbandoned(flow, 'note', exitMethod, content.trim() !== '');
    });
    const handleModalClose = () => {
        end('close');
        onClose();
    };

    const handleCreateNote = async () => {
        if (createNote.isMutating || !fieldId) return;
        if (!content.trim()) {
            setShowErrors(true);
            if (flow)
                mpTrackEvent('Field Registry Submit Attempted', {
                    ...flowEventProps(flow),
                    registry_type: 'note',
                    invalid_fields: ['content'],
                });
            return;
        }
        try {
            await createNote.trigger({ content: content.trim(), field: fieldId });
        } catch (error) {
            if (flow)
                mpTrackEvent('Field Registry Save Failed', {
                    ...flowEventProps(flow),
                    registry_type: 'note',
                    origin: flow.origin,
                    error_detail: errorDetail(error),
                    partial: false,
                });
            // Keep the modal (and the note typed in it) open so the user can retry.
            enqueueSnackbar(_('No se pudo guardar la nota. Intentá nuevamente.'), { variant: 'error' });
            return;
        }
        finish();
        if (flow)
            mpTrackEvent('Field Registry Saved', {
                ...flowEventProps(flow),
                registry_type: 'note',
                origin: flow.origin,
                entry_source: flow.entrySource,
            });
        onSuccess();
    };

    return (
        <ModalV2
            open={open}
            onClose={handleModalClose}
            goBack={() => {
                // Back to the type picker: the attempt goes on there.
                finish();
                setStep('select');
            }}
            title={_('Registrar una nota')}
            bodyProps={{ alignItems: 'stretch', textAlign: 'left', spacing: 2 }}
            actionsProps={{ justifyContent: 'flex-end' }}
            actions={
                <Button
                    variant='contained'
                    color='secondary'
                    disabled={!fieldId || createNote.isMutating}
                    onClick={handleCreateNote}
                >
                    {_('Registrar en el seguimiento')}
                </Button>
            }
        >
            <Stack>
                <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                    {_('Nota')}
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder={_('Escribir una observación')}
                    value={content}
                    onChange={(event) => setContent(event.target.value)}
                    error={contentError}
                    helperText={contentError ? _('Campo requerido') : undefined}
                />
            </Stack>
        </ModalV2>
    );
};

export default CreateRegistryNote;
